import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Image as ImageIcon, Loader2, MessageSquare, X, Tag } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PullItemsEditor = ({ sinceDate, untilDate, labels = [], getAuthHeader }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [noSkuOnly, setNoSkuOnly] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (!sinceDate || !untilDate) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/orders/preview-items?since=${sinceDate}&until=${untilDate}`, getAuthHeader())
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sinceDate, untilDate, getAuthHeader]);

  const patchItem = (id, patch) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const saveNote = async (id, note) => {
    try {
      await axios.patch(`${API}/orders/items/${id}/pull-note`, { note }, getAuthHeader());
      patchItem(id, { pull_note: note });
    } catch {
      toast.error("Failed to save note");
    }
  };

  const uploadImage = async (id, file) => {
    setUploading((u) => ({ ...u, [id]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/orders/items/${id}/pull-image`, fd, {
        headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      patchItem(id, { pull_image_url: data.url });
      toast.success("Image added");
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading((u) => ({ ...u, [id]: false }));
    }
  };

  const saveLabel = async (id, labelId) => {
    try {
      await axios.patch(`${API}/orders/items/${id}/pull-label`, { label_id: labelId || null }, getAuthHeader());
      setItems((prev) => prev.map((i) => {
        if (i.id === id) return { ...i, pull_label_id: labelId };
        if (labelId && i.pull_label_id === labelId) return { ...i, pull_label_id: "" };
        return i;
      }));
      toast.success(labelId ? "Label linked" : "Label unlinked");
    } catch {
      toast.error("Failed to link label");
    }
  };

  const removeImage = async (id) => {
    try {
      await axios.delete(`${API}/orders/items/${id}/pull-image`, getAuthHeader());
      patchItem(id, { pull_image_url: "" });
    } catch {
      toast.error("Failed to remove image");
    }
  };

  const noSkuCount = items.filter((i) => !(i.sku || "").trim()).length;
  const visible = noSkuOnly ? items.filter((i) => !(i.sku || "").trim()) : items;

  return (
    <div className="rounded-lg bg-white border border-[#eee]" data-testid="pull-items-editor">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid="pull-items-toggle">
        <span className="text-xs font-medium text-[#888]">
          Item notes & photos
          {loading ? <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin" /> : ` (${items.length} items${noSkuCount ? `, ${noSkuCount} without SKU` : ""})`}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#bbb]" /> : <ChevronDown className="w-4 h-4 text-[#bbb]" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-[11px] text-[#aaa]">Add a note, photo, or link the shipping label for items the employee can't match by SKU.</p>
          {noSkuCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-[#666] cursor-pointer">
              <input type="checkbox" checked={noSkuOnly} onChange={(e) => setNoSkuOnly(e.target.checked)} className="rounded border-[#ccc]" data-testid="no-sku-only-toggle" />
              Show only items without a SKU ({noSkuCount})
            </label>
          )}
          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="pull-items-list">
            {visible.length === 0 && !loading && <p className="text-xs text-[#aaa] text-center py-3">No items</p>}
            {visible.map((item) => (
              <div key={item.id} className="p-2 rounded-lg bg-[#fafafa] border border-[#eee] space-y-1.5" data-testid={`pull-item-editor-${item.id}`}>
                <div className="flex items-center gap-2">
                  {(item.sku || "").trim() ? (
                    <span className="text-xs font-bold text-[#8B5CF6] font-mono">{item.sku}</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 font-semibold">No SKU</span>
                  )}
                  <span className="text-xs text-[#555] truncate flex-1">{item.title || "Untitled"}</span>
                  {item.platform && <span className="text-[10px] text-[#aaa] capitalize">{item.platform}</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3 text-[#bbb] flex-shrink-0" />
                  <input
                    type="text"
                    defaultValue={item.pull_note || ""}
                    placeholder="Note (e.g., 'Blue Nike shoes, shelf B3')"
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val !== (item.pull_note || "")) saveNote(item.id, val);
                    }}
                    className="flex-1 text-[11px] bg-white border border-[#eee] rounded px-2 py-1 text-[#555] focus:outline-none focus:border-[#8B5CF6]/50"
                    data-testid={`pull-item-note-${item.id}`}
                  />
                  {uploading[item.id] ? (
                    <Loader2 className="w-4 h-4 text-[#8B5CF6] animate-spin" />
                  ) : item.pull_image_url ? (
                    <div className="flex items-center gap-1">
                      <img src={item.pull_image_url} alt="" className="w-7 h-7 rounded object-cover border border-[#ddd]" data-testid={`pull-item-image-${item.id}`} />
                      <button type="button" onClick={() => removeImage(item.id)} className="text-[#ccc] hover:text-red-400" title="Remove photo" data-testid={`pull-item-image-remove-${item.id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer text-[#bbb] hover:text-[#8B5CF6]" title="Add photo">
                      <ImageIcon className="w-4 h-4" />
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => { if (e.target.files[0]) uploadImage(item.id, e.target.files[0]); e.target.value = ""; }}
                        data-testid={`pull-item-image-input-${item.id}`}
                      />
                    </label>
                  )}
                </div>
                {labels.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3 h-3 text-[#bbb] flex-shrink-0" />
                    <select
                      value={item.pull_label_id || ""}
                      onChange={(e) => saveLabel(item.id, e.target.value)}
                      className={`flex-1 text-[11px] bg-white border rounded px-2 py-1 focus:outline-none ${item.pull_label_id ? "border-[#8B5CF6]/40 text-[#8B5CF6] font-medium" : "border-[#eee] text-[#999]"}`}
                      data-testid={`pull-item-label-${item.id}`}
                    >
                      <option value="">Link a shipping label…</option>
                      {labels.map((l) => {
                        const takenBy = items.find((o) => o.id !== item.id && o.pull_label_id === l.id);
                        return (
                          <option key={l.id} value={l.id}>
                            {l.sku_tag ? `${l.sku_tag} — ` : ""}{l.display_name || l.filename}{takenBy ? ` (linked to ${takenBy.sku || "another item"})` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
